import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Clock3, MapPin, Scissors } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import type { PublicShop } from '@agenda-facil/contracts';
import { Button, ScrollReveal, Surface } from '@agenda-facil/ui';
import { Brand } from '../../components/brand';
import { ErrorState, PageLoader } from '../../components/query-feedback';
import { ThemeToggle } from '../../components/theme-toggle';
import { api } from '../../lib/api';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function ShopPage() {
  const { slug = '' } = useParams();
  const shop = useQuery({
    queryKey: ['public-shop', slug],
    queryFn: () => api.get<PublicShop>(`/public/shops/${slug}`),
  });

  if (shop.isLoading) return <PageLoader label="Abrindo a barbearia…" />;
  if (shop.isError || !shop.data)
    return (
      <div className="page-shell py-16">
        <ErrorState message="Essa página ainda não está disponível." />
      </div>
    );

  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--border)]">
        <div className="page-shell flex h-18 items-center justify-between">
          <Brand />
          <ThemeToggle />
        </div>
      </header>
      <main>
        <section className="marketing-grid border-b border-[var(--border)] py-14 sm:py-20">
          <ScrollReveal variant="fade-up" className="page-shell grid items-center gap-10 md:grid-cols-[1fr_auto]">
            <div>
              <span className="eyebrow">Agendamento online</span>
              <h1 className="display mt-5 text-5xl sm:text-7xl">{shop.data.name}</h1>
              <div className="mt-5 flex flex-wrap gap-4 text-sm font-bold text-[var(--muted-foreground)]">
                <span className="flex items-center gap-2">
                  <MapPin size={17} /> {shop.data.address}
                </span>
                <span className="flex items-center gap-2">
                  <Clock3 size={17} /> Horários em tempo real
                </span>
              </div>
            </div>
            <Button size="lg" asChild>
              <Link to={`/barbearia/${slug}/agendar`}>
                Escolher horário <ArrowRight size={18} />
              </Link>
            </Button>
          </ScrollReveal>
        </section>
        <section className="page-shell py-12 sm:py-16">
          <ScrollReveal variant="left" className="flex items-end justify-between">
            <div>
              <span className="eyebrow">Serviços</span>
              <h2 className="display mt-3 text-4xl">Escolha seu cuidado.</h2>
            </div>
          </ScrollReveal>
          <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {shop.data.services.map((service, index) => (
              <ScrollReveal key={service.id} variant="scale" delay={(index + 1) * 80}>
                <Surface className="h-full p-5">
                  <span className="grid size-11 place-items-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
                    <Scissors size={19} />
                  </span>
                  <h3 className="mt-6 font-extrabold">{service.name}</h3>
                  <p className="mt-2 min-h-10 text-sm leading-5 text-[var(--muted-foreground)]">
                    {service.description}
                  </p>
                  <div className="mt-5 flex items-center justify-between border-t border-[var(--border)] pt-4">
                    <span className="text-xs font-bold text-[var(--muted-foreground)]">
                      {service.durationMinutes} min
                    </span>
                    <strong>{currency.format(service.priceCents / 100)}</strong>
                  </div>
                </Surface>
              </ScrollReveal>
            ))}
          </div>
        </section>
      </main>
      <footer className="border-t border-[var(--border)] py-8 text-center text-xs text-[var(--muted-foreground)]">
        Agendamento seguro por Agenda Fácil
      </footer>
    </div>
  );
}

